import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Layout } from 'app/theme/layout'
import {format} from 'date-fns'
import styles from './PostPage.module.css'
import { useAppContext } from 'app/AppContext'
import env from 'env'
import { decodeHtml } from './helpers'
// TODO:
// structured date (JSON-LD) implementation to add to the head
// core web vitals pass
const isDev = env.APP_ENV === 'development'
const isSSR = typeof window === 'undefined'

const protocol = isDev ? 'http://' : 'https://'
const blogUrl = isDev && isSSR ? `${env.INTERNAL_BLOG_URL}/wp-json/wp/v2` : `${protocol + env.INTERNAL_HOST}/wordpress/wp-json/wp/v2`

const fetchBlogPost = async (slug) => {
  try {
    const wpJsonEmbedList = [
      'author',
      'categories',
      'tags',
      'wp:term',
      'wp:featuredmedia'
    ]
    const postFilters = [
      `_embed=${wpJsonEmbedList.join(',')}`,
      `slug=${slug}`
    ]

 
    const queryString = postFilters.join('&')
    const postUrl = `${blogUrl}/posts?${queryString}`
    const rawBlogData = await fetch(postUrl).then(res => res.ok ? res.json() : null)
    return { data: rawBlogData, error: null }
  } catch (error) {
    return { data: null, error: error }
  }
}

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (typeof window === 'undefined') {
    // Server-side rendered fallback
    return html.replace(/<[^>]*>/g, '')
  }
  // Client-side implementation
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const PostPage = () => {
  const { slug } = useParams()
  const context = useAppContext()
  const [post, setPost] = useState(context.blogPost?.data[0])
  const [error, setError] = useState()

  if (typeof window === 'undefined' && context.blogPost === undefined) {
    context.promises = context.promises || []
    context.promises.push(
      fetchBlogPost(slug).then(data => {
        context.blogPost = data
      }).catch(() => {
        context.blogPost = null
      })
    )
  }

  useEffect(() => {
    if (post === undefined) {
      fetchBlogPost(slug).then(({data, error}) => {
        setPost(data[0])
      })
    }
  }, [])

  if (error) {
    return <PostError />
  }

  if (!post) {
    return <PostLoading />
  }

  const title = post.title?.rendered || 'Blog Post'
  const description = post.excerpt?.rendered
    ? stripHtml(post.excerpt.rendered).slice(0, 160)
    : 'Read our latest blog post'
  const imageUrl = post?._embedded['wp:featuredmedia']?.[0]?.source_url

  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "BlogPosting",
    "headline": post.title?.rendered,
    "datePublished": post.date,
    "author": post._embedded?.author[0],
    "description": description,
    "image": imageUrl,
  }

  return (
    <Layout className="container max-w-4xl">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`/blog/${post.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        <link rel="canonical" href={`/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <div className="mb-10">
        <PostContent post={post} />
      </div>
    </Layout>
  )
}

function PostContent({post}) {

  const postCategories = post._embedded['wp:term'].filter(arr => arr.length && arr[0]?.taxonomy === 'category')

  return (
    <article key={post.id} className="flex flex-col">
      <div className="flex-1">
        {post._embedded['wp:featuredmedia'] && (
          <img
            className="w-full border border-gray-300"
            src={post._embedded['wp:featuredmedia'][0]?.source_url}
            alt={post._embedded['wp:featuredmedia'][0]?.alt_text || ''}
            loading="lazy"
            width="100%"
            height="auto"
            style={{
              aspectRatio: '16/9',
            }}
          />
        )}
        <h1 className="text-5xl text-black font-roboto-flex my-2">
          {decodeHtml(post.title?.rendered)}
        </h1>
        <p className="font-light">by {post._embedded?.author[0].name} on {format(new Date(post.date), 'MMM d, yyyy')}</p>
      </div>
      <div className="py-2">
        {postCategories[0]?.map(category =>
          <a
            key={category.slug}
            href={`/blog/category/${category.slug}`}
            className="text-sm font-roboto-flex font-light inline-block bg-gray-800 text-white rounded-full px-2 mr-1"
          >
            {category.name}
          </a>
        )}
      </div>
      <div className={styles.postContent}>
        <div dangerouslySetInnerHTML={{ __html: post.content?.rendered}} />
      </div>
    </article>
  )
}

const PostLoading = () => {
  return <div>Loading...</div>
}

const PostError = () => {
  return <div>Error</div>
}

export default PostPage
